// Scene màn Tu Luyện (UI_SPEC §18.1), theo màn 1a của client tham khảo (viết lại, không import):
//  · NGƯỜI TU LUYỆN: rig Spine `scene/starfield` (bóng người áo choàng giữa chòm sao), đặt đúng
//    hộp của bàn dựng client (cover, zoom 0,75, lệch lên 41px).
//  · KHÍ HẢI ở (195, 385): lõi theo đại cảnh giới (P0: Luyện Khí = ba cụm sương xoay lỏng),
//    vòng trong, vòng ngoài nét đứt ăn màu hướng tu (Tam Nguyên trội), vệt sáng chạy trên vòng.
//  · HẠT LINH KHÍ ba pha: chấm bảy màu ngũ hành + Quang / Ám sinh ở vành xa, trôi chậm và xoáy
//    một chiều; qua nửa đường nén thành sao chổi có đuôi theo quỹ đạo thật; lao vào vòng ngoài và
//    tắt ngay tại điểm chạm, để lại chớp sáng, đôi khi làm vòng phồng.
// Mọi thứ vẽ trong MỘT Application Pixi (Bất biến 9). Không blend kiểu CSS (TECH_BASELINE §11).
import { Container, Graphics, type Application } from 'pixi.js';
import { createRng, type Rng } from '@sim/core/rng';
import { ELEMENTS, hexNum } from '@ui/tokens';
import { glowMesh } from './glow';
import type { SpineLayer } from './spine/loadSpine';

const TAU = Math.PI * 2;
const CX = 195;
const CY = 385;

/** Hình thái khí hải Luyện Khí (khí hải mới mở, chưa có lõi). */
const CORE = {
  loi: 6,
  vongTrong: 24,
  vongNgoai: 44,
  canh: 3,
  canhK: 0.72,
  canhA: 0.26,
  nhip: 5.6,
  net: [4, 9] as const,
  vien: 1.1,
  vienA: 0.42,
  vetLw: 1.6,
  trongA: 0.18,
  quang: 0.62,
  quangMau: 0xbad6e2,
};

// Hạt: vành sinh xa, hút theo luỹ thừa nghịch (chậm ở xa, vọt ở gần), xoáy một chiều.
const SO_HAT = 22;
const R_MIN = 228;
const R_MAX = 358;
const R_THAM = 120;
const V_K = 42;
const V_MU = 1.2;
const XOAY_K = 1.06;
const XOAY_MU = 0.185;
const EP_X = 0.5;
const COMET_U = 0.5;
const COMET_W = 0.32;
const DUOI_N = 9;
const DUOI_DT = 0.045;
const FLASH_S = 0.35;

const HAT_MAU = ELEMENTS.map((e) => hexNum(e.rgb));

interface Hat {
  r: number;
  a: number;
  mau: number;
  co: number;
  toc: number;
  /** Vết quỹ đạo gần nhất (x, y xen kẽ), lấy mẫu theo thời gian. */
  duoi: number[];
  nhipDuoi: number;
}

function keo01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function spawn(h: Hat, rng: Rng, warm: boolean): void {
  h.r = warm ? CORE.vongNgoai + rng.next() * (R_MAX - CORE.vongNgoai) : R_MIN + rng.next() * (R_MAX - R_MIN);
  h.a = rng.next() * TAU;
  h.mau = HAT_MAU[rng.int(HAT_MAU.length)] ?? 0xffffff;
  h.co = 0.8 + rng.next() * 0.4;
  h.toc = 0.85 + rng.next() * 0.3;
  h.duoi = [];
  h.nhipDuoi = 0;
}

export interface CultivationScene {
  readonly root: Container;
  /** Độ mạnh hút theo tốc độ hấp thụ (1 = Luyện Khí online; Tử Quan ≈ 1,6). */
  setIntensity(k: number): void;
  /** Màu vòng ngoài theo hướng tu (Tam Nguyên trội). */
  setTone(color: number): void;
  /** Đột phá tiểu cảnh: vòng phồng + khí hải bừng một nhịp. */
  pulse(): void;
  setVisible(on: boolean): void;
}

export function mountCultivationScene(app: Application, seed: number, assetBase: string): CultivationScene {
  const rng = createRng(seed).fork('cultivationScene');
  const root = new Container();
  root.visible = false;
  app.stage.addChild(root);

  // Lớp 1: người tu luyện (nạp lười; chưa về thì khí hải vẫn chạy).
  const figure = new Container();
  root.addChild(figure);
  let layer: SpineLayer | null = null;
  // pixi-spine nạp động (TECH_BASELINE §9.2: không nằm trong bundle ban đầu).
  void import('./spine/loadSpine')
    .then(async ({ loadSkeleton, placeSpine }) => {
      const data = await loadSkeleton(`${assetBase}assets/spine/scene/starfield/starfield`);
      layer = placeSpine(data, { x: 1, y: -41, w: 390, h: 844, fit: 'cover', zoom: 0.75, anim: 'animation' });
      figure.addChild(layer.node);
    })
    .catch((e: unknown) => console.warn('starfield', e));

  // Lớp 2: quầng khí hải.
  const glow = glowMesh(CORE.quangMau, CORE.vongNgoai * 2.4);
  glow.mesh.position.set(CX, CY);
  root.addChild(glow.mesh);

  // Lớp 3: hạt (dưới vòng để chớp va chạm nằm trên).
  const hatG = new Graphics();
  root.addChild(hatG);
  // Lớp 4: lõi + hai vòng.
  const coreG = new Graphics();
  root.addChild(coreG);

  const hats: Hat[] = [];
  for (let i = 0; i < SO_HAT; i++) {
    const h: Hat = { r: 0, a: 0, mau: 0, co: 1, toc: 1, duoi: [], nhipDuoi: 0 };
    spawn(h, rng, true);
    hats.push(h);
  }
  const flashes: { a: number; t: number; mau: number }[] = [];

  let visible = false;
  let intensity = 1;
  let tone = 0xf0e2be;
  let phong = 0;
  let bung = 0;
  let t = 0;

  const vongNgoai = (): number => CORE.vongNgoai * (1 + 0.07 * phong);

  const pos = (r: number, a: number): [number, number] => {
    const u = keo01((r - CORE.vongNgoai) / (R_MAX - CORE.vongNgoai));
    const ex = 1 - (1 - EP_X) * u;
    return [CX + Math.cos(a) * r * ex, CY + Math.sin(a) * r];
  };

  const stepHats = (dt: number): void => {
    const rv = vongNgoai();
    for (const h of hats) {
      const v = V_K * Math.pow(R_THAM / Math.max(h.r, 1), V_MU) * h.toc * intensity;
      const q = XOAY_K * Math.pow(h.r / R_THAM, XOAY_MU);
      h.r -= v * dt;
      h.a += (q * v * dt) / Math.max(h.r, 1);
      if (h.r <= rv) {
        flashes.push({ a: h.a, t: 0, mau: h.mau });
        if (rng.next() < 1 / 3) phong = Math.min(1, phong + 0.5);
        spawn(h, rng, false);
        continue;
      }
      h.nhipDuoi += dt;
      if (h.nhipDuoi >= DUOI_DT) {
        h.nhipDuoi = 0;
        const [x, y] = pos(h.r, h.a);
        h.duoi.push(x, y);
        if (h.duoi.length > DUOI_N * 2) h.duoi.splice(0, 2);
      }
    }
  };

  const drawHats = (): void => {
    hatG.clear();
    for (const h of hats) {
      const u = keo01((h.r - CORE.vongNgoai) / (R_MAX - CORE.vongNgoai));
      const gan = 1 - u;
      const alpha = (0.22 + 0.55 * gan) * (0.6 + 0.4 * Math.min(1, intensity));
      const size = (0.9 + 1.1 * gan) * h.co;
      const [x, y] = pos(h.r, h.a);
      const comet = keo01((COMET_U - u) / COMET_W);
      if (comet > 0.02 && h.duoi.length >= 4) {
        // Đuôi mọc dần theo hệ số sao chổi; mờ và mảnh dần về phía sau.
        const n = h.duoi.length / 2;
        const shown = Math.max(1, Math.round(n * comet));
        let px = x;
        let py = y;
        for (let i = 0; i < shown; i++) {
          const j = n - 1 - i;
          const qx = h.duoi[j * 2]!;
          const qy = h.duoi[j * 2 + 1]!;
          const k = 1 - i / shown;
          hatG.lineStyle(size * (0.35 + 0.65 * k), h.mau, alpha * k * 0.8);
          hatG.moveTo(px, py).lineTo(qx, qy);
          px = qx;
          py = qy;
        }
        hatG.lineStyle(0);
      }
      hatG
        .beginFill(h.mau, alpha)
        .drawCircle(x, y, size * (1 - 0.35 * comet))
        .endFill();
    }
  };

  const drawCore = (): void => {
    const g = coreG;
    g.clear();
    const tho = 0.5 + 0.5 * Math.sin((t / CORE.nhip) * TAU);
    const rv = vongNgoai();
    // Ba cụm sương xoay lỏng quanh tâm, mép nhoè (vẽ chồng ba lớp mờ).
    for (let i = 0; i < CORE.canh; i++) {
      const a = (t / CORE.nhip) * TAU * 0.6 + (i / CORE.canh) * TAU;
      const d = CORE.vongTrong * CORE.canhK * (0.9 + 0.1 * tho);
      const x = CX + Math.cos(a) * d;
      const y = CY + Math.sin(a) * d * 0.9;
      for (let k = 3; k >= 1; k--)
        g.beginFill(CORE.quangMau, (CORE.canhA / 3) * (1 + bung))
          .drawCircle(x, y, 4 + k * 3.2)
          .endFill();
    }
    g.beginFill(0xf4f1e6, 0.35 + 0.25 * tho + 0.3 * bung)
      .drawCircle(CX, CY, CORE.loi * (0.8 + 0.2 * tho))
      .endFill();
    // Vòng trong liền, mờ.
    g.lineStyle(1, CORE.quangMau, CORE.trongA * (1 + bung)).drawCircle(CX, CY, CORE.vongTrong);
    // Vòng ngoài nét đứt ăn màu hướng tu.
    const [dash, gap] = CORE.net;
    const step = (dash + gap) / rv;
    const len = dash / rv;
    const quay = t * 0.12;
    g.lineStyle(CORE.vien, tone, CORE.vienA * (1 + 0.6 * bung));
    for (let a = 0; a < TAU; a += step) {
      g.moveTo(CX + Math.cos(a + quay) * rv, CY + Math.sin(a + quay) * rv);
      g.arc(CX, CY, rv, a + quay, a + quay + len);
    }
    // Vệt sáng chạy trên vòng ngoài.
    const va = (t / CORE.nhip) * TAU;
    g.lineStyle(CORE.vetLw, tone, 0.55 + 0.3 * bung);
    g.moveTo(CX + Math.cos(va) * rv, CY + Math.sin(va) * rv);
    g.arc(CX, CY, rv, va, va + 0.7);
    // Chớp sáng nơi hạt chạm vòng.
    for (const f of flashes) {
      const k = 1 - f.t / FLASH_S;
      g.lineStyle(2, f.mau, 0.8 * k);
      g.moveTo(CX + Math.cos(f.a - 0.18) * rv, CY + Math.sin(f.a - 0.18) * rv);
      g.arc(CX, CY, rv, f.a - 0.18, f.a + 0.18);
    }
    g.lineStyle(0);
    glow.uniforms.uAlpha = (0.28 + 0.1 * tho) * CORE.quang + 0.35 * bung;
  };

  const tick = (): void => {
    if (!visible) return;
    const dt = Math.min(app.ticker.deltaMS / 1000, 0.1);
    t += dt;
    phong = Math.max(0, phong - dt * 2.2);
    bung = Math.max(0, bung - dt * 1.1);
    for (let i = flashes.length - 1; i >= 0; i--) {
      const f = flashes[i]!;
      f.t += dt;
      if (f.t >= FLASH_S) flashes.splice(i, 1);
    }
    layer?.update(dt);
    stepHats(dt * (1 + bung * 1.5));
    drawHats();
    drawCore();
  };
  app.ticker.add(tick);

  return {
    root,
    setIntensity(k) {
      intensity = Math.max(0.2, Math.min(2, k));
    },
    setTone(c) {
      tone = c;
    },
    pulse() {
      bung = 1;
      phong = 1;
    },
    setVisible(on) {
      visible = on;
      root.visible = on;
    },
  };
}
